import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionReveal from './ui/SectionReveal';
import TapToBoop from './ui/TapToBoop';
import BuyCard from './ui/BuyCard';
import Contracts from './ui/Contracts';
import StickyBuy from './ui/StickyBuy';
import Seo from './ui/Seo';
import { cn } from '../lib/cn';
import { ALL_PARTNERS, BOOP_STEPS, FAQ as FAQ_ITEMS, FEATURES, LINKS, SOCIALS } from '../data/site';
import boopImage from '../assets/images/boop.jpg';
import boopHowTo from '../assets/images/boop-how-to.jpg';
import thegame from '../assets/newImages/thegame.png';

const Hero = () => (
  <section id="top" className="pt-5 lg:pt-8" aria-labelledby="hero-title">
    <SectionReveal>
      <div className="overflow-hidden rounded-3xl border-[3px] border-ink bg-white shadow-hard-lg">
        <img
          src="/assets/boop-banner.jpg"
          alt="BOOP logo and the dog, ready for a boop"
          className="h-auto w-full lg:h-[280px] lg:object-cover lg:object-[center_35%]"
          width={1280}
          height={426}
        />
      </div>
    </SectionReveal>

    <div className="mt-5 grid items-center gap-2 lg:mt-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] lg:gap-14">
      <SectionReveal className="text-center lg:text-left">
        <h1 id="hero-title" className="text-[44px] font-bold leading-[1.05] tracking-[0.5px] sm:text-[56px] lg:text-[76px]">
          Everyone deserves <span className="hl">a boop.</span>
        </h1>
        <p className="mx-auto mt-4 max-w-[440px] text-[17px] font-medium lg:mx-0 lg:mt-6 lg:max-w-[520px] lg:text-xl">
          Meet BOOP, <strong className="rounded-lg bg-ink px-2 py-px text-sun">Arbitrum&apos;s best doggo</strong>. A memecoin built on belly rubs,
          mini games, and MemeFi rewards. Live on Arbitrum and Solana.
        </p>
        <div className="mt-8 hidden flex-wrap gap-4 lg:flex">
          <a className="btn px-7 py-3 text-lg" href="#buy">
            Buy $BOOP
          </a>
          <a className="btn btn-paper px-7 py-3 text-lg" href={LINKS.game}>
            Play the game 🎮
          </a>
          <a className="btn btn-paper px-7 py-3 text-lg" href="#contracts">
            Contracts
          </a>
        </div>
      </SectionReveal>

      <SectionReveal delay={0.08}>
        <TapToBoop />
      </SectionReveal>
    </div>
  </section>
);

const BuySection = () => (
  <section className="grid items-start gap-3.5 pt-5 lg:grid-cols-2 lg:gap-10 lg:pt-16">
    <SectionReveal id="buy">
      <BuyCard />
      <p className="mt-3 text-center text-sm font-medium">
        Prefer a DEX or another EVM chain?{' '}
        <Link to="/buy" className="font-semibold underline">
          See all ways to buy →
        </Link>
      </p>
    </SectionReveal>
    <SectionReveal delay={0.06}>
      <Contracts />
    </SectionReveal>
  </section>
);

const Features = () => (
  <section id="features" className="grid items-stretch gap-7 pt-7 lg:grid-cols-12 lg:gap-8 lg:pt-16">
    <SectionReveal className="lg:col-span-5">
      <div className="card h-full">
        <div className="art mb-3.5">
          <video
            src="/assets/boop-crown.mp4"
            poster={boopImage}
            autoPlay
            loop
            muted
            playsInline
            className="aspect-square w-full object-cover"
            aria-label="BOOP the dog wearing a crown, looping video"
          />
        </div>
        <h2 className="card-title">
          <span className="mr-1.5">👑</span>Arbitrum&apos;s best doggo
        </h2>
        <p className="section-sub !mb-0">
          Crowned by the community, fueled by boops. Born on Arbitrum for faster, cheaper zoomies, now also fetching on Solana. When the market
          gets ruff, BOOP keeps wagging.
        </p>
      </div>
    </SectionReveal>

    <div className="grid gap-3.5 sm:grid-cols-2 lg:col-span-7 lg:gap-5">
      <SectionReveal className="sm:col-span-2">
        <div className="rounded-[22px] border-[3px] border-ink bg-sun px-4 py-4 shadow-hard-lg lg:px-6 lg:py-6">
          <h2 className="text-2xl font-bold leading-tight lg:text-[32px]">Welcome to MemeFi 🐶</h2>
          <p className="mt-1.5 text-[15px] font-medium lg:text-lg">
            Where viral culture meets on-chain markets: loud, lovable and community-powered. Fun fuels the flywheel, finance follows the boop.
          </p>
          <Link to="/memefi" className="btn btn-paper btn-sm mt-3">
            Explore MemeFi →
          </Link>
        </div>
      </SectionReveal>
      {FEATURES.map((f, i) => (
        <SectionReveal key={f.title} delay={i * 0.05}>
          <div className="flex h-full items-start gap-3 rounded-2xl border-[3px] border-ink bg-paper px-4 py-3.5 shadow-hard sm:block lg:px-5 lg:py-5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-[3px] border-ink bg-sun text-xl sm:mb-2">{f.emoji}</div>
            <div>
              <h3 className="text-lg font-bold lg:text-xl">{f.title}</h3>
              <p className="mt-0.5 text-sm lg:text-[15px]">{f.body}</p>
            </div>
          </div>
        </SectionReveal>
      ))}
    </div>
  </section>
);

const GameCard = () => (
  <section id="game" className="pt-7 lg:pt-16">
    <SectionReveal>
      <div className="card card-ink grid items-center gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:gap-10 lg:!p-10">
        <div className="order-2 lg:order-1">
          <h2 className="card-title !text-sun lg:!text-[40px]">
            <span className="mr-1.5">🎮</span>BOOP THE GAME
          </h2>
          <p className="section-sub lg:!text-lg">The official boop simulator. Whack-a-BOOP: how many noses can you boop before you run out of lives?</p>
          <a className="btn w-full px-10 py-3.5 text-xl lg:mt-2 lg:w-auto" href={LINKS.game}>
            PLAY NOW
          </a>
        </div>
        <img
          src={thegame}
          alt="BOOP The Game logo"
          className="order-1 mx-auto h-auto max-h-36 w-auto lg:order-2 lg:max-h-[240px]"
          width={877}
          height={560}
          loading="lazy"
        />
      </div>
    </SectionReveal>
  </section>
);

const HowToBoop = () => (
  <section id="how" className="pt-7 lg:pt-16">
    <SectionReveal>
      <div className="card grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-center lg:gap-10">
        <div className="art hidden lg:block">
          <img src={boopHowTo} alt="How to boop: a finger heading for the dog's nose" className="h-auto w-full" width={959} height={623} loading="lazy" />
        </div>
        <div>
          <h2 className="card-title">
            <span className="mr-1.5">👆</span>How to BOOP
          </h2>
          <p className="section-sub">The sacred four-step technique. Playful and non-invasive, always.</p>
          <ol className="grid gap-2.5 sm:grid-cols-2 lg:gap-4">
            {BOOP_STEPS.map((s, i) => (
              <li key={s.title} className="flex items-start gap-3 rounded-2xl border-[3px] border-ink bg-paper px-3.5 py-3 shadow-hard">
                <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full border-[3px] border-ink bg-sun text-lg font-bold">
                  {i + 1}
                </span>
                <div>
                  <h3 className="text-base font-bold">{s.title}</h3>
                  <p className="text-sm">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </SectionReveal>
  </section>
);

const FAQ = () => {
  const [open, setOpen] = useState(null);

  return (
    <SectionReveal id="faq">
      <div className="card">
        <h2 className="card-title">
          <span className="mr-1.5">💡</span>Get Started with BOOP
        </h2>
        <p className="section-sub">
          Quick answers before you boop your wallet. More questions?{' '}
          <a className="underline" href={LINKS.telegram} target="_blank" rel="noopener noreferrer">
            Ask in Telegram
          </a>
          .
        </p>
        <div className="grid gap-2.5">
          {FAQ_ITEMS.map((item, i) => {
            const expanded = open === i;
            return (
              <div key={item.q} className="overflow-hidden rounded-2xl border-[3px] border-ink bg-paper">
                <button
                  type="button"
                  id={`faq-q-${i}`}
                  aria-expanded={expanded}
                  aria-controls={`faq-a-${i}`}
                  onClick={() => setOpen(expanded ? null : i)}
                  className="flex w-full cursor-pointer items-center justify-between gap-2.5 bg-paper px-4 py-3.5 text-left text-base font-semibold lg:text-[17px]"
                >
                  {item.q}
                  <span className={cn('shrink-0 text-[22px] font-bold leading-none transition-transform', expanded && 'rotate-45')} aria-hidden="true">
                    +
                  </span>
                </button>
                {expanded && (
                  <div id={`faq-a-${i}`} role="region" aria-labelledby={`faq-q-${i}`} className="px-4 pb-3.5 text-[15px]">
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </SectionReveal>
  );
};

const Pack = () => (
  <SectionReveal id="pack" delay={0.06}>
    <div className="card">
      <div className="art mb-3.5">
        <img src="/assets/boop-pack.jpg" alt="The BOOP pack running together" className="h-auto w-full" width={1000} height={666} loading="lazy" />
      </div>
      <h2 className="card-title">
        <span className="mr-1.5">🐾</span>Join the Pack
      </h2>
      <p className="section-sub">Memes, games, and chaos, everywhere. Everyone deserves a boop.</p>
      <div className="grid grid-cols-2 gap-2.5">
        {SOCIALS.map((s) => (
          <a key={s.href} className="btn btn-paper px-2 py-3 text-[15px]" href={s.href} target="_blank" rel="noopener noreferrer">
            {s.label}
          </a>
        ))}
        <a className="btn col-span-2 px-2 py-3 text-[15px]" href={LINKS.coingecko} target="_blank" rel="noopener noreferrer">
          CoinGecko ↗
        </a>
      </div>
    </div>
  </SectionReveal>
);

const PartnersStrip = () => (
  <section id="partners" className="pt-7 lg:pt-16">
    <SectionReveal>
      <div className="card">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="card-title">
              <span className="mr-1.5">🤝</span>MemeFi friends
            </h2>
            <p className="section-sub">Partners, protocols and co-conspirators in the boopiverse.</p>
          </div>
          <Link to="/memefi" className="btn btn-paper btn-sm mb-3">
            All partners →
          </Link>
        </div>
        <ul className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 lg:grid-cols-6 lg:gap-4">
          {ALL_PARTNERS.map((p) => (
            <li key={p.name}>
              <a
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-full flex-col items-center justify-center gap-1.5 rounded-2xl border-2 border-ink bg-white px-2 py-3 no-underline shadow-hard-sm transition-transform hover:-translate-y-0.5 active:translate-x-0.5 active:translate-y-0.5 active:shadow-none"
              >
                <img src={p.logo} alt="" className="h-10 w-auto max-w-full object-contain lg:h-12" width={80} height={48} loading="lazy" />
                <span className="text-center text-[11px] font-semibold lg:text-xs">{p.name}</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </SectionReveal>
  </section>
);

const HomePage = () => (
  <>
    <Seo />
    <main className="wrap">
      <Hero />
      <BuySection />
      <Features />
      <GameCard />
      <HowToBoop />
      <section className="grid items-start gap-7 pt-7 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-8 lg:pt-12">
        <FAQ />
        <Pack />
      </section>
      <PartnersStrip />
    </main>
    <StickyBuy />
  </>
);

export default HomePage;
